import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { FinanceBalanceSummaryResponseDto } from '@/features/finances/types/finances-dto';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';
import { useCurrencyStore } from '@/store/useCurrencyStore.ts';
import { DateTimeHelper } from '@/utils/dateTimeHelper.ts';

type Props = {
    ownerId?: string;
}

export const useFinancesBalanceSummaryApi = ({ ownerId }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useFinancesPageFilter();
    const { currentCurrency } = useCurrencyStore();
    const { endDate, apartmentId } = filter;
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);

    const {
        data: balanceSummaryData,
        isLoading: isLoadingBalanceSummaryData,
        isError: isErrorBalanceSummaryData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.finances.queryKeys.balanceSummary,
            apartmentIds,
            ownerId,
            endDate,
            currentCurrency,
        ],
        queryFn: async (): Promise<FinanceBalanceSummaryResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.features.finances.endpoints.balanceSummary,
                {
                    apartmentId: apartmentIds,
                    ownerId: ownerId,
                    endDate: DateTimeHelper.getDateOnly(endDate),
                },
            );
            const response = await axiosInstance.get<FinanceBalanceSummaryResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        balanceSummaryData,
        isLoadingBalanceSummaryData,
        isErrorBalanceSummaryData,
    };
};
